// Withdraw.tsx - Single centered column on desktop, compact mobile header without back arrow

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useSolanaBalances } from "@/hooks/use-solana-balances";
import { TradeErrorDialog } from "@/components/trade-error-dialog";
import { ArrowLeft, Loader2, CheckCircle, Copy, Shield, AlertCircle, ExternalLink, QrCode, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Sanitize error messages to prevent XSS
function sanitizeError(error: any): string {
  if (!error || typeof error !== 'string') return 'An error occurred';
  return error.replace(/<[^>]*>/g, '').substring(0, 200);
}

// ============ MULTI-CHAIN TOKEN CONFIGURATION ============

const ALL_TOKENS = [
  "SOL", "USDT", "USDC", "FIXERCOIN", "PINGX", "LOCKER", "FXM", "JUP", "BONK", "PYTH", "ORCA", "RAY", "JTO", "MEW", "WIF", "POPCAT",
  "ETH", "BNB", "MATIC", "BTC", "DAI", "UNI", "LINK", "CAKE",
];

const TOKEN_CHAINS: Record<string, string> = {
  SOL: "solana", USDT: "solana", USDC: "solana", FIXERCOIN: "solana", PINGX: "solana", LOCKER: "solana", FXM: "solana",
  JUP: "solana", BONK: "solana", PYTH: "solana", ORCA: "solana", RAY: "solana", JTO: "solana", MEW: "solana", WIF: "solana", POPCAT: "solana",
  ETH: "ethereum", BNB: "bsc", MATIC: "polygon", BTC: "bitcoin", DAI: "ethereum", UNI: "ethereum", LINK: "ethereum", CAKE: "bsc",
};

const CHAIN_DISPLAY_NAMES: Record<string, string> = {
  solana: "SOLANA",
  ethereum: "ETHEREUM",
  bsc: "BSC",
  polygon: "POLYGON",
  bitcoin: "BITCOIN",
};

// ============ HARDCODED NATIVE TOKEN PRICES (USD) ============
const NATIVE_TOKEN_PRICES: Record<string, number> = {
  SOL: 93,
  BNB: 653,
  MATIC: 1.3,
  BTC: 80000,
  ETH: 2310,
  TRON: 1.2,
};

// ============ FEE CONFIGURATION ============
const FIXED_SOL_FEE = 0.005;
const TARGET_FEE_USD = 0.10;

const ADDRESS_VALIDATORS: Record<string, (address: string) => boolean> = {
  solana: (addr) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr),
  ethereum: (addr) => /^0x[a-fA-F0-9]{40}$/.test(addr),
  bsc: (addr) => /^0x[a-fA-F0-9]{40}$/.test(addr),
  polygon: (addr) => /^0x[a-fA-F0-9]{40}$/.test(addr),
  bitcoin: (addr) => /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,90}$/.test(addr),
};

const FEE_ADDRESSES: Record<string, string> = {
  solana: "9PsJzqtusfNNEBNMF7AoTE5YuU7CBSMW8ccoPan15MDd",
  ethereum: "0x2e15E2157F5401F64072c9e4f24112E63cD79428",
  bsc: "0x2e15E2157F5401F64072c9e4f24112E63cD79428",
  polygon: "0x2e15E2157F5401F64072c9e4f24112E63cD79428",
  bitcoin: "bc1qcwevcwy4wz5lzwmtusxakt47c7kslvmaw2m3vp",
};

// ============ COMPONENTS ============

// QR Scanner modal (camera-based)
const QRScannerModal = ({ show, onClose, onScan }: { show: boolean; onClose: () => void; onScan: (value: string) => void }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<any>(null);
  const rafRef = useRef<number | null>(null);
  const [error, setError] = useState<string>("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!show) return;

    let cancelled = false;

    const start = async () => {
      setError("");
      setStarting(true);

      // Check support
      if (!("mediaDevices" in navigator) || !navigator.mediaDevices?.getUserMedia) {
        setError("Camera not supported on this device/browser.");
        setStarting(false);
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }

        // Use BarcodeDetector if available
        const BarcodeDetectorCtor: any = (window as any).BarcodeDetector;
        if (BarcodeDetectorCtor) {
          detectorRef.current = new BarcodeDetectorCtor({ formats: ["qr_code"] });
          const tick = async () => {
            if (!videoRef.current || !detectorRef.current) return;
            try {
              const results = await detectorRef.current.detect(videoRef.current);
              if (results && results.length > 0) {
                const value = results[0].rawValue;
                if (value) {
                  onScan(value);
                  onClose();
                  return;
                }
              }
            } catch {
              // ignore per-frame errors
            }
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setError("Live QR detection not supported in this browser. Enter the address manually or use a supported browser (Chrome/Edge).");
        }
      } catch (e: any) {
        setError(e?.message || "Unable to access camera. Please grant permission and try again.");
      } finally {
        setStarting(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      detectorRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [show, onScan, onClose]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-card text-foreground rounded-lg p-5 border border-border space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-400">Scan QR Code</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative w-full aspect-square bg-black rounded-lg overflow-hidden border border-border">
          <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
          {starting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
            </div>
          )}
          {/* Scan frame */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-2/3 h-2/3 border-2 border-yellow-400 rounded-lg" />
          </div>
        </div>

        {error && (
          <p className="text-[10px] text-red-400 uppercase text-center">{error}</p>
        )}

        <Button onClick={onClose} variant="outline" className="w-full border-border text-foreground hover:bg-muted text-[10px] uppercase">
          Close
        </Button>
      </div>
    </div>
  );
};

const WithdrawalNotificationCard = ({ show, onConfirm, onCancel, amount = "", token = "", feeAmount = "", chain = "", address = "" }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-sm bg-card text-foreground rounded-lg p-6 space-y-4 border border-primary/30">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-foreground">WITHDRAWAL DETAILS</h2>
        <div className="space-y-3 border-t border-border pt-4">
          <div className="flex justify-between items-center">
            <span className="text-[11px] uppercase text-muted-foreground">AMOUNT:</span>
            <span className="text-[11px] uppercase font-semibold text-foreground">{amount} {token}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[11px] uppercase text-muted-foreground">PLATFORM FEE:</span>
            <span className="text-[11px] uppercase font-semibold text-foreground">{feeAmount} {token}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[11px] uppercase text-muted-foreground">NETWORK:</span>
            <span className="text-[11px] uppercase font-semibold text-foreground">{CHAIN_DISPLAY_NAMES[chain] || chain}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[11px] uppercase text-muted-foreground">RECIPIENT:</span>
            <span className="text-[11px] uppercase font-semibold text-foreground">{address.slice(0, 8)}...{address.slice(-6)}</span>
          </div>
        </div>
        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={onCancel} className="flex-1 text-[12px] uppercase border-border text-foreground hover:bg-muted">CANCEL</Button>
          <Button onClick={onConfirm} className="flex-1 bg-yellow-400 text-black hover:bg-yellow-500 text-[12px] uppercase font-semibold">CONFIRM</Button>
        </div>
      </div>
    </div>
  );
};

const ProcessingDialog = ({ show, message = "Processing your withdrawal...", isSuccess = false, txSignature = "", amount = "", token = "", chain = "", onClose = null }) => {
  const [copied, setCopied] = useState(false);
  if (!show) return null;
  const handleCopy = async () => { if (txSignature) { await navigator.clipboard.writeText(txSignature); setCopied(true); setTimeout(() => setCopied(false), 2000); } };
  const getExplorerUrl = () => {
    if (!txSignature || txSignature === "pending") return null;
    switch (chain) {
      case "solana": return `https://solscan.io/tx/${txSignature}`;
      case "ethereum": return `https://etherscan.io/tx/${txSignature}`;
      case "bsc": return `https://bscscan.com/tx/${txSignature}`;
      case "polygon": return `https://polygonscan.com/tx/${txSignature}`;
      default: return null;
    }
  };
  if (isSuccess) {
    const explorerUrl = getExplorerUrl();
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-sm bg-card text-foreground rounded-lg p-8 space-y-6 border border-green-500">
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"><CheckCircle className="w-12 h-12 text-green-500" /></div>
            <div><h2 className="text-xl font-bold text-foreground">Withdrawal Successful!</h2><p className="text-sm text-muted-foreground mt-2">Your funds have been sent</p></div>
            <div className="w-full border-t border-border pt-4">
              <p className="text-foreground text-lg font-semibold mb-2">{amount} {token}</p>
              <p className="text-xs text-muted-foreground mb-2">Network: {CHAIN_DISPLAY_NAMES[chain] || chain.toUpperCase()}</p>
              {txSignature && txSignature !== "pending" && (
                <div className="flex items-center justify-center gap-2">
                  <div onClick={handleCopy} className="text-sm font-mono flex items-center gap-2 cursor-pointer text-primary hover:text-primary/80">
                    <span>{txSignature.slice(0,8)}...{txSignature.slice(-8)}</span>
                    <Copy className="w-4 h-4" />
                  </div>
                  {explorerUrl && <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80"><ExternalLink className="w-4 h-4" /></a>}
                  {copied && <span className="text-xs text-green-500 ml-1">Copied!</span>}
                </div>
              )}
            </div>
            <Button onClick={onClose} className="w-full bg-green-500 text-white hover:bg-green-600">Done</Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-sm bg-card text-foreground rounded-lg p-8 space-y-6 border border-primary/30">
        <div className="flex flex-col items-center text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"><Loader2 className="w-12 h-12 text-primary animate-spin" /></div>
          <div><h2 className="text-xl font-bold text-foreground">Processing Withdrawal</h2><p className="text-sm text-muted-foreground mt-2">{message}</p></div>
          <div className="w-full"><div className="h-1 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary animate-pulse rounded-full w-2/3"></div></div></div>
        </div>
      </div>
    </div>
  );
};

const PasswordDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [password, setPassword] = useState("");
  if (!show) return null;
  const handleSubmit = () => { if (password.trim() && !loading) { onSubmit(password); setPassword(""); } };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-bold text-foreground mb-4">Enter Your Password</h3>
        <p className="text-sm text-muted-foreground mb-4">Your password is required to decrypt your wallet and complete this withdrawal.</p>
        {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-500">{error}</div>}
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="mb-4 bg-background border-border text-foreground" onKeyPress={(e) => e.key === "Enter" && handleSubmit()} autoFocus disabled={loading} />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!password.trim() || loading} className="flex-1 bg-yellow-400 text-black hover:bg-yellow-500">{loading ? "Processing..." : "Confirm"}</Button>
        </div>
      </div>
    </div>
  );
};

const BackupFileDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [manualBackupText, setManualBackupText] = useState("");
  const [manualError, setManualError] = useState("");

  if (!show) return null;

  const validateBackupContent = (content: string): boolean => {
    if (content.length > 1024 * 1024) throw new Error("File too large. Maximum size is 1MB.");
    const parsed = JSON.parse(content);
    if (!parsed.magic || !parsed.salt || !parsed.iv || !parsed.data) throw new Error("Invalid backup file structure.");
    if (parsed.magic !== "FIXORIUM-MPC") throw new Error("Invalid backup file format.");
    return true;
  };

  const handleManualSubmit = () => {
    if (!manualBackupText.trim()) {
      setManualError("Please paste your backup JSON");
      return;
    }
    try {
      validateBackupContent(manualBackupText);
      onSubmit(manualBackupText);
      setManualBackupText("");
      setManualError("");
    } catch (err: any) {
      setManualError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4">
        {(error || manualError) && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-500">{error || manualError}</div>}
        
        <div className="space-y-3">
          <textarea
            value={manualBackupText}
            onChange={(e) => setManualBackupText(e.target.value)}
            placeholder='Paste your backup JSON here...'
            className="w-full h-32 bg-muted border border-border rounded-lg p-2 text-[10px] font-mono text-foreground resize-none focus:outline-none focus:border-yellow-400/50"
          />
          <Button onClick={handleManualSubmit} disabled={loading} className="w-full bg-yellow-400 text-black hover:bg-yellow-500 text-sm">
            Verify & Use
          </Button>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ============ WITHDRAWAL FORM (with inline QR scan) ============
const WithdrawalForm = React.memo(({ 
  withdrawToken, withdrawAmount, withdrawAddress,
  loading, showProcessing, balancesLoading, availableTokens, tokenBalances,
  processingSuccess, selectedChain,
  onTokenChange, onAmountChange, onAddressChange, onWithdraw, onCancel, isMobile,
  onScanClick,
}: any) => {
  const maxBalance = tokenBalances[withdrawToken] || 0;
  const chainDisplay = CHAIN_DISPLAY_NAMES[selectedChain] || selectedChain?.toUpperCase() || "SOLANA";
  
  return (
    <div className={cn(
      "bg-card",
      isMobile ? "p-0 border-0 rounded-none bg-transparent" : "p-6 rounded-xl border border-border"
    )}>
      <div className="space-y-6">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">TOKEN</label>
          <select value={withdrawToken} onChange={onTokenChange} className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-[11px] uppercase focus:outline-none focus:ring-2 focus:ring-yellow-400" disabled={loading || showProcessing || balancesLoading}>
            {availableTokens.length > 0 ? availableTokens.map(({ token, balance }: any) => <option key={token} value={token} className="bg-background text-foreground">{token} ({balance.toFixed(6)}) - {TOKEN_CHAINS[token]?.toUpperCase()}</option>) : <option disabled className="bg-background text-foreground">NO TOKENS AVAILABLE</option>}
          </select>
          <p className="text-[10px] text-muted-foreground mt-2 uppercase">{balancesLoading ? "Loading balances..." : `Available: ${maxBalance.toFixed(6)} ${withdrawToken}`}</p>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">NETWORK</label>
          <div className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-[10px] uppercase">{chainDisplay}</div>
          <p className="text-[11px] text-muted-foreground mt-2 uppercase">{selectedChain === "solana" ? "Send only to Solana addresses" : selectedChain === "bitcoin" ? "Send only to Bitcoin addresses" : "Send only to EVM compatible addresses (0x...)"}</p>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">RECIPIENT ADDRESS</label>
          <div className="mt-2 flex items-center gap-2">
            <Input value={withdrawAddress} onChange={onAddressChange} placeholder={selectedChain === "solana" ? "PASTE SOLANA WITHDRAWAL ADDRESS" : selectedChain === "bitcoin" ? "PASTE BITCOIN WITHDRAWAL ADDRESS" : "PASTE 0x... WITHDRAWAL ADDRESS"} className="flex-1 text-[10px] bg-background border-border text-foreground" disabled={loading || showProcessing} />
            <Button
              type="button"
              onClick={onScanClick}
              disabled={loading || showProcessing}
              className="shrink-0 h-9 px-3 bg-yellow-400 hover:bg-yellow-500 text-black"
              title="Scan QR code"
            >
              <QrCode className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 uppercase">{selectedChain === "solana" ? "Solana address: 32-44 characters (base58)" : selectedChain === "bitcoin" ? "Bitcoin address: bc1 or legacy format" : "EVM address: 0x-prefixed 40 hex characters"}</p>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">AMOUNT</label>
          <div className="mt-2"><Input value={withdrawAmount} onChange={onAmountChange} placeholder="0.0" className="w-full text-[10px] bg-background border-border text-foreground" inputMode="decimal" autoComplete="off" disabled={loading || showProcessing} /></div>
        </div>
        <div className="flex gap-2 pt-4">
          <Button onClick={onWithdraw} disabled={loading || !withdrawAmount || !withdrawAddress || showProcessing || balancesLoading || availableTokens.length === 0} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-[10px] uppercase">{showProcessing ? (processingSuccess ? "SUCCESS!" : "PROCESSING...") : balancesLoading ? "LOADING BALANCES..." : "REQUEST WITHDRAW"}</Button>
          <Button variant="outline" onClick={onCancel} disabled={loading || showProcessing} className="flex-1 text-[10px] uppercase border-border text-foreground hover:bg-muted">CANCEL</Button>
        </div>
      </div>
    </div>
  );
});

// ============ MAIN COMPONENT ============
export default function Withdraw() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [loading, setLoading] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [processingSuccess, setProcessingSuccess] = useState(false);
  const [successTxHash, setSuccessTxHash] = useState("");
  const [successAmount, setSuccessAmount] = useState("");
  const [successToken, setSuccessToken] = useState("");
  const [successChain, setSuccessChain] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawToken, setWithdrawToken] = useState("SOL");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [selectedChain, setSelectedChain] = useState("solana");
  const [tokenBalances, setTokenBalances] = useState<Record<string, number>>({});
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorType, setErrorType] = useState<"user-not-found" | "hash-not-received" | "withdrawal-failed">("withdrawal-failed");
  const [errorEmail, setErrorEmail] = useState("");
  const [user, setUser] = useState<any | null>(null);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const [pendingWithdrawalData, setPendingWithdrawalData] = useState<any>(null);
  const [passwordError, setPasswordError] = useState("");
  const [backupError, setBackupError] = useState("");
  const [loadingUser, setLoadingUser] = useState(true);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);
  const [successTimer, setSuccessTimer] = useState<NodeJS.Timeout | null>(null);
  const [backupFileContent, setBackupFileContent] = useState<string | null>(null);
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [showWithdrawalNotification, setShowWithdrawalNotification] = useState(false);
  const [notificationData, setNotificationData] = useState<any>(null);
  const [showQRScanner, setShowQRScanner] = useState(false);

  // Calculate fee based on token (no price fetching needed)
  useEffect(() => {
    if (!withdrawToken) return;
    
    const chain = TOKEN_CHAINS[withdrawToken];
    
    if (chain === "solana") {
      setFeeAmount(FIXED_SOL_FEE);
    } else {
      let nativePrice = 0;
      switch (chain) {
        case "ethereum":
          nativePrice = NATIVE_TOKEN_PRICES.ETH;
          break;
        case "bsc":
          nativePrice = NATIVE_TOKEN_PRICES.BNB;
          break;
        case "polygon":
          nativePrice = NATIVE_TOKEN_PRICES.MATIC;
          break;
        case "bitcoin":
          nativePrice = NATIVE_TOKEN_PRICES.BTC;
          break;
        default:
          nativePrice = 1;
      }
      
      if (nativePrice > 0) {
        const feeInNative = TARGET_FEE_USD / nativePrice;
        setFeeAmount(feeInNative);
      } else {
        setFeeAmount(0.001);
      }
    }
  }, [withdrawToken]);

  useEffect(() => {
    if (withdrawToken) {
      const chain = TOKEN_CHAINS[withdrawToken];
      setSelectedChain(chain || "solana");
      setWithdrawAddress("");
    }
  }, [withdrawToken]);

  const getWalletAddress = useCallback(() => {
    if (!user?.wallets) return "";
    switch (selectedChain) {
      case "solana": return user.wallets.solana || "";
      case "ethereum": return user.wallets.ethereum || "";
      case "bsc": return user.wallets.bsc || "";
      case "polygon": return user.wallets.polygon || "";
      case "bitcoin": return user.wallets.bitcoin || "";
      default: return "";
    }
  }, [user, selectedChain]);

  const solanaWalletAddress = getWalletAddress();
  const { balances: solanaBalances, loading: solanaBalancesLoading } = useSolanaBalances({
    walletAddress: selectedChain === "solana" ? solanaWalletAddress : "",
    refetchInterval: 30000,
    enabled: selectedChain === "solana" && !!solanaWalletAddress,
  });

  const fetchEVMBalances = useCallback(async () => {
    if (!user || selectedChain === "solana") return {};
    try {
      const response = await fetch(`/api/transfer-evm?action=get-balances&userId=${user.email}&chain=${selectedChain}`, { credentials: "include" });
      const data = await response.json();
      if (data.success && data.balances) return data.balances;
      return {};
    } catch (error) { console.error(`[Withdraw] Failed to fetch ${selectedChain} balances:`, error); return {}; }
  }, [user, selectedChain]);

  const fetchBitcoinBalance = useCallback(async () => {
    if (!user || selectedChain !== "bitcoin") return {};
    try {
      const response = await fetch(`/api/transfer-bitcoin?action=get-balances&userId=${user.email}`, { credentials: "include" });
      const data = await response.json();
      if (data.success && data.balances) return data.balances;
      return {};
    } catch (error) { console.error("[Withdraw] Failed to fetch Bitcoin balances:", error); return {}; }
  }, [user, selectedChain]);

  useEffect(() => {
    const loadBalances = async () => {
      setBalancesLoading(true);
      if (selectedChain === "solana") {
        if (solanaBalances && Object.keys(solanaBalances).length > 0) {
          setTokenBalances(solanaBalances);
        }
      } else if (selectedChain === "bitcoin") {
        const btcBalances = await fetchBitcoinBalance();
        setTokenBalances(btcBalances);
      } else {
        const evmBalances = await fetchEVMBalances();
        setTokenBalances(evmBalances);
      }
      setBalancesLoading(false);
    };
    loadBalances();
  }, [solanaBalances, selectedChain, fetchEVMBalances, fetchBitcoinBalance]);

  const availableTokens = Object.entries(tokenBalances)
    .filter(([token, balance]) => {
      const chain = TOKEN_CHAINS[token];
      return chain === selectedChain && balance > 0;
    })
    .map(([token, balance]) => {
      return { token, balance };
    });

  useEffect(() => {
    if (availableTokens.length > 0 && !availableTokens.some(t => t.token === withdrawToken)) {
      setWithdrawToken(availableTokens[0].token);
      setWithdrawAmount("");
    }
  }, [availableTokens, withdrawToken]);

  useEffect(() => {
    return () => { if (pollInterval) clearInterval(pollInterval); if (successTimer) clearTimeout(successTimer); };
  }, [pollInterval, successTimer]);

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setWithdrawAmount(e.target.value), []);
  const handleAddressChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setWithdrawAddress(e.target.value), []);
  const handleTokenChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => { setWithdrawToken(e.target.value); setWithdrawAmount(""); }, []);
  const handleCancel = useCallback(() => navigate("/dashboard"), [navigate]);

  const validateAddress = (address: string, chain: string): boolean => {
    const validator = ADDRESS_VALIDATORS[chain];
    return validator ? validator(address) : false;
  };

  async function executeWithdrawal(withdrawalData: any, password: string, backupFile: string) {
    if (!user) return;
    try {
      setPasswordError("");
      setBackupError("");
      setProcessingMessage("Processing withdrawal with MPC...");
      
      const response = await fetch("/api/wallet?action=withdraw", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userId: user.email,
          token: withdrawToken,
          amount: withdrawalData.amount,
          feeAmount: withdrawalData.feeAmount,
          to_address: withdrawalData.to_address,
          feeAddress: withdrawalData.feeAddress,
          password,
          backupFile,
          useMPC: true,
        }),
      });
      
      console.log("[Withdraw] Response status:", response.status);
      
      if (response.status === 401) {
        const data = await response.json();
        setLoading(false); 
        setShowProcessing(false);
        const errorMsg = data.details || data.error || "Invalid password or backup file. Please try again.";
        setPasswordError(sanitizeError(errorMsg));
        setShowPasswordPrompt(true);
        return;
      }
      
      const data = await response.json();
      
      if (data.success && data.txHash) {
        setPendingWithdrawalData(null);
        setSuccessTxHash(data.txHash);
        setSuccessAmount(withdrawalData.amount.toFixed(6));
        setSuccessToken(withdrawToken);
        setSuccessChain(selectedChain);
        setProcessingSuccess(false);
        setTimeout(() => setProcessingSuccess(true), 10);
        setLoading(false);
        
        const timer = setTimeout(() => { 
          setShowProcessing(false); 
          setProcessingSuccess(false); 
          setSuccessTxHash(""); 
          setSuccessAmount(""); 
          setSuccessToken(""); 
          setSuccessChain(""); 
          navigate("/dashboard"); 
        }, 10000);
        setSuccessTimer(timer);
        
        toast({ 
          title: "✅ Withdrawal Completed!", 
          description: `Sent ${withdrawalData.amount.toFixed(6)} ${withdrawToken}` 
        });
        return;
      }
      
      if (response.status === 202 && data.transactionId) {
        setPendingWithdrawalData(null);
        setProcessingMessage("Withdrawal submitted, waiting for confirmation...");
        let attempts = 0;
        const maxAttempts = 15;
        const interval = setInterval(async () => {
          attempts++;
          setProcessingMessage(`Confirming transaction... (${attempts}/${maxAttempts})`);
          try {
            const statusRes = await fetch(`/api/wallet?action=transaction-status&id=${data.transactionId}`, { credentials: "include" });
            const statusData = await statusRes.json();
            if (statusData.status === "completed") {
              clearInterval(interval);
              if (successTimer) clearTimeout(successTimer);
              setSuccessTxHash(statusData.transaction.transactionSignature);
              setSuccessAmount(withdrawalData.amount.toFixed(6));
              setSuccessToken(withdrawToken);
              setSuccessChain(selectedChain);
              setProcessingSuccess(false);
              setTimeout(() => setProcessingSuccess(true), 10);
              setLoading(false);
              const timer = setTimeout(() => { 
                setShowProcessing(false); 
                setProcessingSuccess(false); 
                setSuccessTxHash(""); 
                setSuccessAmount(""); 
                setSuccessToken(""); 
                setSuccessChain(""); 
                navigate("/dashboard"); 
              }, 10000);
              setSuccessTimer(timer);
              toast({ 
                title: "✅ Withdrawal Completed!", 
                description: `Sent ${withdrawalData.amount.toFixed(6)} ${withdrawToken}` 
              });
            } else if (statusData.status === "failed") {
              clearInterval(interval);
              setShowProcessing(false);
              setProcessingSuccess(false);
              toast({ 
                title: "Withdrawal Failed", 
                description: statusData.transaction?.error || "Transaction failed", 
                variant: "destructive" 
              });
              setLoading(false);
            }
          } catch (err) { 
            console.error("Error checking status:", err); 
          }
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            setShowProcessing(false);
            setProcessingSuccess(false);
            toast({ 
              title: "Status Timeout", 
              description: "Transaction may still be processing. Check your wallet balance." 
            });
            setLoading(false);
          }
        }, 3000);
        setPollInterval(interval);
        return;
      }
      
      if (data.error) {
        setLoading(false); 
        setShowProcessing(false);
        toast({ title: "Withdrawal Failed", description: sanitizeError(data.error), variant: "destructive" });
        return;
      }
      
      setLoading(false); 
      setShowProcessing(false);
      toast({ 
        title: "Withdrawal Failed", 
        description: data.message || "Unknown error occurred", 
        variant: "destructive" 
      });
      
    } catch (err: any) {
      console.error("[Withdraw] Error:", err);
      setPendingWithdrawalData(null); 
      setLoading(false); 
      setShowProcessing(false); 
      setProcessingSuccess(false);
      toast({ 
        title: "Withdrawal Failed", 
        description: sanitizeError(err.message), 
        variant: "destructive" 
      });
    }
  }

  async function doWithdraw() {
    if (!user) { toast({ title: "Authentication Required", description: "Please log in to withdraw", variant: "destructive" }); navigate("/login"); return; }
    
    const amt = parseFloat(withdrawAmount);
    if (isNaN(amt) || amt <= 0) { return toast({ title: "INVALID AMOUNT", description: "Please enter an amount greater than 0", variant: "destructive" }); }
    
    const trimmedAddress = withdrawAddress.trim();
    if (!trimmedAddress) { return toast({ title: "INVALID ADDRESS", description: "Please enter a withdrawal address", variant: "destructive" }); }
    
    if (!validateAddress(trimmedAddress, selectedChain)) {
      let expectedFormat = "";
      if (selectedChain === "solana") expectedFormat = "32-44 character base58 address";
      else if (selectedChain === "bitcoin") expectedFormat = "bitcoin address (bc1 or legacy)";
      else expectedFormat = "0x-prefixed 40-character hex address";
      return toast({ title: "INVALID ADDRESS FORMAT", description: `Please enter a valid ${CHAIN_DISPLAY_NAMES[selectedChain]} address (${expectedFormat})`, variant: "destructive" });
    }
    
    if (balancesLoading) { toast({ title: "Loading Balances", description: "Please wait while we fetch your current balances..." }); return; }
    
    const totalBalance = tokenBalances[withdrawToken] || 0;
    let availableBalance = totalBalance;
    
    let feeInTokens = feeAmount;
    
    if (selectedChain === "solana") {
      feeInTokens = FIXED_SOL_FEE;
    }
    
    const totalRequired = amt + feeInTokens;
    
    if (totalRequired > totalBalance) {
      return toast({
        title: "INSUFFICIENT BALANCE",
        description: `You need ${totalRequired.toFixed(6)} ${withdrawToken} (${amt.toFixed(6)} withdrawal + ${feeInTokens.toFixed(6)} fee). Your balance: ${totalBalance.toFixed(6)} ${withdrawToken}`,
        variant: "destructive"
      });
    }
    
    if (amt < 0.00005) {
      return toast({
        title: "WITHDRAWAL AMOUNT TOO SMALL",
        description: `Minimum withdrawal amount is 0.00005 ${withdrawToken}.`,
        variant: "destructive"
      });
    }


    const feeAddress = FEE_ADDRESSES[selectedChain];
    if (!feeAddress) {
      toast({ title: "Fee Address Missing", description: "Platform fee address not configured for this network.", variant: "destructive" });
      return;
    }

    const withdrawalData = {
      userId: user.email,
      token: withdrawToken,
      amount: amt,
      feeAmount: feeInTokens,
      to_address: trimmedAddress,
      feeAddress: feeAddress,
      chain: selectedChain,
    };

    setNotificationData({
      amount: amt.toFixed(6),
      token: withdrawToken,
      feeAmount: feeInTokens.toFixed(6),
      chain: selectedChain,
      address: trimmedAddress,
    });
    setPendingWithdrawalData(withdrawalData);
    setShowWithdrawalNotification(true);
  }

  const handlePasswordSubmit = async (enteredPassword: string) => {
    if (!enteredPassword?.trim()) { setPasswordError("Please enter your password"); return; }
    if (!pendingWithdrawalData) { setPasswordError("No pending withdrawal data"); return; }
    if (!backupFileContent) { setShowPasswordPrompt(false); setShowBackupPrompt(true); return; }
    setLoading(true); setShowProcessing(true); setProcessingSuccess(false); setProcessingMessage("Verifying password and preparing transaction..."); setPasswordError(""); setShowPasswordPrompt(false);
    try { await executeWithdrawal(pendingWithdrawalData, enteredPassword, backupFileContent); } catch (error: any) { setLoading(false); setShowProcessing(false); setProcessingSuccess(false); setPasswordError(error.message || "An unexpected error occurred"); toast({ title: "Withdrawal Failed", description: error.message || "An unexpected error occurred", variant: "destructive" }); }
  };

  const handleBackupSubmit = async (backupContent: string) => {
    if (!backupContent) { setBackupError("Please paste your backup JSON"); return; }
    try {
      if (backupContent.length > 1024 * 1024) throw new Error("File too large. Maximum size is 1MB.");
      const backup = JSON.parse(backupContent);
      if (!backup.magic || !backup.salt || !backup.iv || !backup.data) throw new Error("Invalid backup file structure. Missing required fields.");
      if (backup.magic !== "FIXORIUM-MPC") throw new Error("Invalid backup file format. Expected FIXORIUM-MPC magic bytes.");
    } catch (err: any) { setBackupError(sanitizeError(err.message) || "Invalid backup file. Please paste the correct JSON."); return; }
    if (!pendingWithdrawalData) { setBackupError("No pending withdrawal data"); return; }
    setBackupFileContent(backupContent);
    setBackupError("");
    setShowBackupPrompt(false);
    setShowPasswordPrompt(true);
  };

  const handleWithdrawalNotificationConfirm = () => {
    setShowWithdrawalNotification(false);
    if (!backupFileContent) {
      setShowBackupPrompt(true);
      return;
    }
    setShowPasswordPrompt(true);
  };

  const handleWithdrawalNotificationCancel = () => {
    setShowWithdrawalNotification(false);
    setPendingWithdrawalData(null);
    setNotificationData(null);
  };

  const handleCancelPassword = () => { setShowPasswordPrompt(false); setPasswordError(""); setPendingWithdrawalData(null); setShowProcessing(false); setProcessingSuccess(false); };
  const handleCancelBackup = () => { setShowBackupPrompt(false); setBackupError(""); setPendingWithdrawalData(null); };
  const closeProcessingDialog = () => { setShowProcessing(false); setProcessingSuccess(false); setSuccessTxHash(""); setSuccessAmount(""); setSuccessToken(""); setSuccessChain(""); if (pollInterval) clearInterval(pollInterval); if (successTimer) clearTimeout(successTimer); navigate("/dashboard"); };

  const handleQRScanResult = useCallback((value: string) => {
    // QR codes may contain "solana:ADDRESS?..." or plain address — extract plain address
    let scanned = value.trim();
    const uriMatch = scanned.match(/^[a-z]+:([^?]+)/i);
    if (uriMatch) scanned = uriMatch[1];
    setWithdrawAddress(scanned);
    toast({ title: "QR SCANNED", description: "Address captured from QR code." });
  }, []);

  // ============ SECURE USER LOADING ============
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            setLoadingUser(false);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[Withdraw] Auth error:", error);
        navigate("/login");
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, [navigate]);

  if (loadingUser) {
    return (
      <section className="w-full min-h-screen bg-background text-foreground">
        <div className="w-full px-[6px] py-6">
          <div className="flex items-center gap-3 mb-8">
            <div>
              <p className="text-xs text-muted-foreground">LOADING...</p></div></div>
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>
        </div>
      </section>
    );
  }

  if (!user && !showErrorDialog) { navigate("/login"); return null; }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TradeErrorDialog open={showErrorDialog} onOpenChange={setShowErrorDialog} type={errorType} email={errorEmail} />
      {isDesktop ? (
        // DESKTOP: single centered column
        <div className="p-6 min-h-screen flex justify-center">
          <div className="w-full max-w-xl">
            <WithdrawalForm
              withdrawToken={withdrawToken} withdrawAmount={withdrawAmount} withdrawAddress={withdrawAddress}
              loading={loading} showProcessing={showProcessing} balancesLoading={balancesLoading}
              availableTokens={availableTokens} tokenBalances={tokenBalances} processingSuccess={processingSuccess}
              selectedChain={selectedChain}
              onTokenChange={handleTokenChange} onAmountChange={handleAmountChange}
              onAddressChange={handleAddressChange} onWithdraw={doWithdraw} onCancel={handleCancel}
              isMobile={false}
              onScanClick={() => setShowQRScanner(true)}
            />
          </div>
        </div>
      ) : (
        // MOBILE: compact header, no back arrow
        <section className="w-full min-h-screen bg-background text-foreground px-[11px]">
          
<div className="space-y-6 pt-6">
              <WithdrawalForm
                withdrawToken={withdrawToken} withdrawAmount={withdrawAmount} withdrawAddress={withdrawAddress}
                loading={loading} showProcessing={showProcessing} balancesLoading={balancesLoading}
                availableTokens={availableTokens} tokenBalances={tokenBalances} processingSuccess={processingSuccess}
                selectedChain={selectedChain}
                onTokenChange={handleTokenChange} onAmountChange={handleAmountChange}
                onAddressChange={handleAddressChange} onWithdraw={doWithdraw} onCancel={handleCancel}
                isMobile={true}
                onScanClick={() => setShowQRScanner(true)}
              />
            </div>
          
        </section>
      )}
      <WithdrawalNotificationCard
        show={showWithdrawalNotification}
        onConfirm={handleWithdrawalNotificationConfirm}
        onCancel={handleWithdrawalNotificationCancel}
        amount={notificationData?.amount || ""}
        token={notificationData?.token || ""}
        feeAmount={notificationData?.feeAmount || ""}
        chain={notificationData?.chain || ""}
        address={notificationData?.address || ""}
      />
      <PasswordDialog show={showPasswordPrompt} onClose={handleCancelPassword} onSubmit={handlePasswordSubmit} error={passwordError} loading={loading} />
      <BackupFileDialog show={showBackupPrompt} onClose={handleCancelBackup} onSubmit={handleBackupSubmit} error={backupError} loading={loading} />
      <ProcessingDialog show={showProcessing} message={processingMessage} isSuccess={processingSuccess} txSignature={successTxHash} amount={successAmount} token={successToken} chain={successChain} onClose={closeProcessingDialog} />
      <QRScannerModal show={showQRScanner} onClose={() => setShowQRScanner(false)} onScan={handleQRScanResult} />
    </div>
  );
}
